import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, Heart, User, Users, LogOut, Menu, ShieldCheck, ShoppingBag, Smartphone, X, Zap } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useLanguage, LANGS } from "../i18n";
import { useAuth } from "../auth";
import { isStandaloneApp } from "../utils/pwa";

export default function Nav() {
  const { lang, setLang, t, currency, setCurrency } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header data-testid="nav-header" className="sticky top-0 z-50 backdrop-blur-xl bg-[#FBF9F5]/85 border-b border-[var(--border-subtle)]">
      <div className="px-3 sm:px-8 lg:px-16 max-w-7xl mx-auto h-14 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="nav-logo" className="font-display font-black text-xl tracking-tight flex items-center">
          <img src="/favicon.svg" alt="SIMAMMA" data-testid="nav-logo-mark" className="h-8 w-8 sm:hidden" />
          <span className="hidden sm:inline">SIMAMMA<span className="text-[var(--accent-terracotta)]">.</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/destinations" data-testid="nav-destinations-link" className="font-mono-t text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("nav.destinations")}
          </Link>
          <Link to="/how-it-works" data-testid="nav-howitworks-link" className="font-mono-t text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("nav.howItWorks")}
          </Link>
          <Link to="/topup" data-testid="nav-topup-link" className="font-mono-t text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("acc.topup")}
          </Link>
          <Link to="/account?tab=esims" data-testid="nav-myesims-link" className="font-mono-t text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("nav.myEsims")}
          </Link>
          <Link to="/account?tab=wishlist" data-testid="nav-wishlist-link" className="inline-flex items-center gap-1.5 font-mono-t text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            <Heart className="w-3.5 h-3.5" /> {t("nav.wishlist")}
          </Link>
          <Link to="/business" data-testid="nav-business-link" className="font-mono-t text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("biz.nav")}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="language-switcher-select" className="flex items-center gap-1.5 rounded-full border border-[var(--border-strong)] px-3 py-1.5 font-mono-t text-xs uppercase tracking-widest hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
                <Globe className="w-3.5 h-3.5" />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[85vh] overflow-y-auto">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`lang-option-${l.code}`} onClick={() => setLang(l.code)} className={`py-1.5 ${l.code === lang ? "font-bold text-[var(--accent-terracotta)]" : ""}`}>
                  <span className="font-mono-t text-xs uppercase w-7">{l.code}</span>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="currency-switcher-select" className="hidden sm:inline-block rounded-full border border-[var(--border-strong)] px-3 py-1.5 font-mono-t text-xs font-bold uppercase tracking-widest hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
                {currency}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {["USD", "EUR", "GBP", "JPY", "PLN", "TRY"].map((c) => (
                <DropdownMenuItem key={c} data-testid={`currency-option-${c.toLowerCase()}`} onClick={() => setCurrency(c)} className={c === currency ? "font-bold text-[var(--accent-terracotta)]" : ""}>
                  {c}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-button" className="flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-4 py-1.5 font-mono-t text-xs font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors shadow-sm shadow-orange-200">
                  <User className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {user.is_admin && (
                  <>
                    <DropdownMenuItem data-testid="account-menu-admin" onClick={() => navigate("/admin")}>
                      <ShieldCheck className="w-4 h-4 mr-2" /> {t("nav.admin")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="account-menu-admin-orders" onClick={() => navigate("/admin?tab=orders")}>
                      <ShoppingBag className="w-4 h-4 mr-2" /> {t("admin.orders")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="account-menu-admin-customers" onClick={() => navigate("/admin?tab=customers")}>
                      <Users className="w-4 h-4 mr-2" /> {t("admin.customers")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="account-menu-admin-esims" onClick={() => navigate("/admin?tab=esims")}>
                      <Smartphone className="w-4 h-4 mr-2" /> eSIM
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem data-testid="account-menu-profile" onClick={() => navigate("/account?tab=esims")}>
                  <User className="w-4 h-4 mr-2" /> {t("nav.myEsims")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="account-menu-logout" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/account" data-testid="nav-signin-button" className="rounded-full bg-[#FF6B35] text-white px-4 py-1.5 font-mono-t text-xs font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors shadow-sm shadow-orange-200">
              {t("nav.signIn")}
            </Link>
          )}

          <button data-testid="mobile-menu-button" className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (isStandaloneApp() ? (
        // In app: menu smart — pochi pulsanti grandi con icone, le 4 azioni chiave
        <div data-testid="mobile-menu-app" className="md:hidden border-t border-[var(--border-subtle)] px-4 py-4 bg-[#FBF9F5] grid grid-cols-2 gap-3">
          {[
            ["/destinations", ShoppingBag, t("nav.destinations"), "app-nav-buy"],
            ["/account", Smartphone, t("nav.myEsims"), "app-nav-sims"],
            ["/topup", Zap, t("acc.topup"), "app-nav-topup"],
            ["/account?tab=wishlist", Heart, t("nav.wishlist"), "app-nav-wishlist"],
          ].map(([to, Icon, label, tid]) => (
            <Link key={tid} to={to} data-testid={tid} onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-4 py-4 shadow-sm hover:border-[#FF6B35] transition-colors">
              <span className="w-9 h-9 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4" />
              </span>
              <span className="font-display font-bold text-sm text-slate-900">{label}</span>
            </Link>
          ))}
        </div>
      ) : (
        <div className="md:hidden border-t border-[var(--border-subtle)] px-4 py-2 flex flex-col bg-[#FBF9F5]">
          <Link to="/destinations" data-testid="mobile-nav-destinations" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] font-mono-t text-sm uppercase tracking-[0.2em]">
            {t("nav.destinations")}
          </Link>
          <Link to="/how-it-works" data-testid="mobile-nav-howitworks" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] font-mono-t text-sm uppercase tracking-[0.2em]">
            {t("nav.howItWorks")}
          </Link>
          <Link to="/topup" data-testid="mobile-nav-topup" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] font-mono-t text-sm uppercase tracking-[0.2em] text-[#FF6B35] font-bold">
            {t("acc.topup")}
          </Link>
          <Link to="/account" data-testid="mobile-nav-account" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] font-mono-t text-sm uppercase tracking-[0.2em]">
            {t("nav.myEsims")}
          </Link>
          <Link to="/account?tab=wishlist" data-testid="mobile-nav-wishlist" onClick={() => setOpen(false)} className="flex items-center gap-2 min-h-[44px] font-mono-t text-sm uppercase tracking-[0.2em]">
            <Heart className="w-4 h-4" /> {t("nav.wishlist")}
          </Link>
          <Link to="/business" data-testid="mobile-nav-business" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] font-mono-t text-sm uppercase tracking-[0.2em]">
            {t("biz.nav")}
          </Link>
          <div className="mt-1 pt-3 pb-1 border-t border-[var(--border-subtle)] flex items-center gap-2 flex-wrap">
            {["USD", "EUR", "GBP", "JPY", "PLN", "TRY"].map((c) => (
              <button key={c} type="button" data-testid={`mobile-currency-${c.toLowerCase()}`} onClick={() => setCurrency(c)}
                className={`rounded-full border px-3.5 min-h-[36px] font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  c === currency ? "border-[#FF6B35] text-[#FF6B35]" : "border-slate-200 text-slate-500"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      ))}
    </header>
  );
}
